import { dataHora } from "../../lib/formato.js";

/**
 * Hora relativa para a fila de impressão: "há 3 min", "há 2 h".
 *
 * Quem olha a fila quer saber há quanto tempo a folha foi mandada, não a hora cheia: "há 3 min" diz se vale
 * esperar mais um pouco, "14:22" obriga a fazer a conta. Passado um dia a data volta a ser mais útil que a
 * contagem, e aí cai no formato que o resto do sistema já usa.
 *
 * `agora` entra por parâmetro: é o que deixa o teste fixar o relógio, e a tela redesenhar a cada meio
 * minuto sem consultar `Date.now()` em cada linha.
 */
export function horaRelativa(valor: string | Date | null | undefined, agora: Date): string {
  if (!valor) return "—";
  const quando = valor instanceof Date ? valor : new Date(valor);
  const t = quando.getTime();
  if (Number.isNaN(t)) return "—";

  // O relógio do servidor pode estar poucos segundos à frente do aparelho: "daqui a 2 s" seria absurdo.
  const segundos = Math.max(0, Math.round((agora.getTime() - t) / 1000));
  if (segundos < 45) return "agora há pouco";

  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `há ${minutos} min`;

  const horas = Math.round(minutos / 60);
  if (horas < 24) return `há ${horas} h`;

  return dataHora(quando);
}

/**
 * "Enviada agora", "Enviada há 3 min", "Enviada em 01/10 09:00": a hora relativa com um verbo na frente,
 * que `horaRelativa` sozinha não permite — "Enviada agora há pouco" soa errado, e "Enviada 01/10 09:00",
 * sem o "em", também.
 */
export function comVerbo(
  verbo: string,
  valor: string | Date | null | undefined,
  agora: Date,
): string {
  const relativa = horaRelativa(valor, agora);
  if (relativa === "—") return verbo;
  if (relativa === "agora há pouco") return `${verbo} agora`;
  return relativa.startsWith("há ") ? `${verbo} ${relativa}` : `${verbo} em ${relativa}`;
}
